import React, { useState, useEffect, useRef } from 'react';
import { 
  Container, Paper, Typography, Box, 
  IconButton, Button, Grid, TextField,
  Dialog, DialogContent, DialogTitle, DialogActions,
  Snackbar, Alert, useMediaQuery, useTheme,
  Divider
} from '@mui/material';
import FavoriteIcon from '@mui/icons-material/Favorite';
import ShareIcon from '@mui/icons-material/Share';
import CloseIcon from '@mui/icons-material/Close';
import ShareDialog from '../components/ShareDialog';
import AdvertisementModal from '../components/AdvertisementModal';
// 导入观看积分 API
import watchApi, { WatchProgressTracker } from '../services/watchApi';
import { addFavorite, checkFavorite, removeFavorite } from '../services/favoriteApi';
// 导入认证 store
import { useAuthStore } from '../stores/authStore';

const DramaDetail = ({ match }) => {
  const [drama, setDrama] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isShareDialogOpen, setIsShareDialogOpen] = useState(false); // 新增状态用于控制分享弹窗
  const [isFavoriteSnackbarOpen, setIsFavoriteSnackbarOpen] = useState(false); // 新增状态用于控制收藏成功弹窗
  const [isPlayerOpen, setIsPlayerOpen] = useState(false);
  const [selectedEpisode, setSelectedEpisode] = useState(null);
  const [comments, setComments] = useState([]); // 新增：评论状态
  const [newComment, setNewComment] = useState(''); // 新增：新评论输入状态
  const [isAddingComment, setIsAddingComment] = useState(false); // 新增：添加评论时的加载状态
  const [advertisement, setAdvertisement] = useState(null); // 新增：广告状态
  const [showAd, setShowAd] = useState(false); // 新增：广告显示状态
  const [sharePointsSnackbar, setSharePointsSnackbar] = useState({ open: false, points: 0, message: '' }); // 分享积分提示
  const [favoritePointsSnackbar, setFavoritePointsSnackbar] = useState({ open: false, points: 0, message: '' }); // 收藏积分提示
  const [isLoginRequiredDialogOpen, setIsLoginRequiredDialogOpen] = useState(false); // 登录提示弹窗状态
  const [isFavorited, setIsFavorited] = useState(false); // 是否已收藏状态
  const videoRef = useRef(null); // 新增：视频引用
  const progressTrackerRef = useRef(null); // 观看进度跟踪器

  // 获取当前登录用户（支持双系统）
  const { mongoUser } = useAuthStore();
  
  // 检查是否已登录（MongoDB 用户或 JSON 用户）
  const checkIsLoggedIn = () => {
    // MongoDB 用户登录
    if (mongoUser && mongoUser._id) {
      return true;
    }
    // 旧版 JSON 用户登录
    const storedUser = localStorage.getItem('user');
    if (storedUser) {
      const user = JSON.parse(storedUser);
      if (user && user.id) {
        return true;
      }
    }
    return false;
  };

  // 获取当前登录用户信息（支持双系统）
  const getCurrentUser = () => {
    // MongoDB 用户登录
    if (mongoUser && mongoUser._id) {
      return {
        id: mongoUser._id,
        name: mongoUser.email || mongoUser.username || mongoUser.name || '用户'
      };
    }
    // 旧版 JSON 用户登录
    const storedUser = localStorage.getItem('user');
    if (storedUser) {
      const user = JSON.parse(storedUser);
      if (user && user.id) {
        return {
          id: user.id,
          name: user.email || user.username || user.name || '用户'
        };
      }
    }
    return null;
  };

  // 检查是否已登录
  const isLoggedIn = checkIsLoggedIn();
  const currentUser = getCurrentUser();

  const id = match.params.id;

  useEffect(() => {
    console.log('广告状态更新:', advertisement);
  }, [advertisement]);

  // ==================== 观看进度上报 ====================
  // 当播放弹窗打开 + 选中剧集 + 用户已登录时，给 video 绑定 timeupdate/ended 事件
  // 通过 WatchProgressTracker 节流上报到 /api/v2/watch/progress
  useEffect(() => {
    if (!isPlayerOpen || !selectedEpisode || !drama) return;

    // 解析用户ID（兼容 mongoUser 与 localStorage user）
    let userId = null;
    if (mongoUser) {
      userId = mongoUser._id || mongoUser.id || mongoUser.user_id;
    } else {
      try {
        const userStr = localStorage.getItem('mongo_user') || localStorage.getItem('user');
        if (userStr) {
          const u = JSON.parse(userStr);
          userId = u._id || u.id || u.user_id;
        }
      } catch (e) {}
    }

    if (!userId) {
      console.log('⚠️ 未登录，跳过观看进度上报');
      return;
    }

    const dramaId = drama.id || drama._id || id;
    // episode 对象只有 sort_order 字段（无 id/_id/name），优先用集数序号
    // 兜底用当前 episodes 数组中的索引 + 1
    const episodeIndex = drama.episodes?.findIndex(ep => ep === selectedEpisode);
    const episodeId =
      selectedEpisode.sort_order ||
      selectedEpisode.id ||
      selectedEpisode._id ||
      (episodeIndex >= 0 ? episodeIndex + 1 : 1);

    // 等 video 元素挂载（Dialog 内 ref 在下一个 tick 才有值）
    const timer = setTimeout(() => {
      const videoEl = videoRef.current;
      if (!videoEl) {
        console.warn('⚠️ video 元素未就绪，跳过绑定');
        return;
      }

      // 销毁旧跟踪器
      if (progressTrackerRef.current) {
        progressTrackerRef.current.destroy();
      }

      // 创建新跟踪器：每 30 秒上报一次
      progressTrackerRef.current = new WatchProgressTracker(userId, dramaId, episodeId, 30);
      console.log('✅ 观看进度跟踪器已创建', { userId, dramaId, episodeId });

      const handleTimeUpdate = async () => {
        if (!progressTrackerRef.current || !videoEl.duration) return;
        await progressTrackerRef.current.update(videoEl.currentTime, videoEl.duration);
      };

      const handleEnded = async () => {
        if (progressTrackerRef.current) {
          await progressTrackerRef.current.onPlayEnd(100);
          console.log('✅ 播放结束，已上报 100% 进度');
        }
      };

      videoEl.addEventListener('timeupdate', handleTimeUpdate);
      videoEl.addEventListener('ended', handleEnded);

      // 把清理函数存到 ref 上供下面 cleanup 使用
      progressTrackerRef.current._cleanup = () => {
        videoEl.removeEventListener('timeupdate', handleTimeUpdate);
        videoEl.removeEventListener('ended', handleEnded);
      };
    }, 100);

    return () => {
      clearTimeout(timer);
      if (progressTrackerRef.current) {
        // 关闭弹窗前强制上报一次最后进度
        const videoEl = videoRef.current;
        if (videoEl && videoEl.duration && videoEl.currentTime > 0) {
          const finalProgress = Math.min(100, Math.round((videoEl.currentTime / videoEl.duration) * 100));
          progressTrackerRef.current.onPlayEnd(finalProgress);
        }
        if (progressTrackerRef.current._cleanup) {
          progressTrackerRef.current._cleanup();
        }
        progressTrackerRef.current.destroy();
        progressTrackerRef.current = null;
      }
    };
  }, [isPlayerOpen, selectedEpisode, drama, mongoUser, id]);

  useEffect(() => {
    fetchDramaDetail();
    fetchAd(); // 新增：获取广告
  }, [id]);

  const fetchDramaDetail = async () => {
    try {
      setIsLoading(true);
      const response = await fetch(`/api/dramas/${id}`);
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      
      if (data.success && data.data) {
        setDrama(data.data);
      } else {
        throw new Error('Invalid data structure');
      }

      // 同时获取评论数据
      fetchComments();

      // 检查用户是否已收藏该剧集
      if (checkIsLoggedIn()) {
        let userId = null;
        if (mongoUser && mongoUser._id) {
          userId = mongoUser._id;
        } else {
          const storedUser = localStorage.getItem('user');
          if (storedUser) {
            const user = JSON.parse(storedUser);
            if (user && user.id) {
              userId = user.id;
            }
          }
        }
        
        if (userId) {
          try {
            const checkResult = await checkFavorite({ user_id: userId, drama_id: id });
            if (checkResult.success) {
              setIsFavorited(checkResult.data.is_favorited);
            }
          } catch (e) {
            console.warn('检查收藏状态失败:', e);
          }
        }
      }
    } catch (error) {
      console.error('获取剧集详情失败:', error);
      setError(error.message);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchComments = async () => {
    try {
      const response = await fetch(`/api/dramas/${id}/comments`);
      if (response.ok) {
        const data = await response.json();
        // 直接使用数据数组，不再检查 success 和 data 字段
        if (Array.isArray(data)) {
          // 按照评论时间排序，最新的在最上面
          const sortedComments = [...data].sort((a, b) => 
            new Date(b.commentTime) - new Date(a.commentTime)
          );
          setComments(sortedComments);
        } else {
          setComments([]);
        }
      } else {
        console.error('获取评论失败');
        setComments([]);
      }
    } catch (error) {
      console.error('获取评论失败:', error);
      setComments([]);
    }
  };

  const fetchAd = async () => {
    try {
      const response = await fetch('/api/advertisements/active/first');
      console.log('广告 API 响应:', response);
      if (response.ok) {
        const result = await response.json();
        const ad = result.data;
        console.log('广告数据:', ad);
        console.log('广告图片字段:', ad.imageUrl);
        console.log('广告图片字段（下划线）:', ad.image_url);
        if (ad && ad.isActive) {
          setAdvertisement(ad);
        }
      }
    } catch (error) {
      console.error('获取广告失败:', error);
    }
  };

  const handleShare = () => {
    setIsShareDialogOpen(true); // 打开分享弹窗
  };

  const handleCloseShareDialog = () => {
    setIsShareDialogOpen(false);
  };

  // 处理分享完成后的积分奖励
  const handleShareComplete = async (platform) => {
    console.log(`DramaDetail: 用户分享到 ${platform}`);

    // 获取用户ID（支持 MongoDB 和旧版 JSON 系统）
    let userId = null;
    if (mongoUser) {
      userId = mongoUser._id || mongoUser.id;
    } else {
      try {
        const userStr = localStorage.getItem('user');
        if (userStr) {
          const user = JSON.parse(userStr);
          userId = user._id || user.id || user.user_id;
        }
      } catch (e) {
        console.warn('读取用户信息失败:', e);
      }
    }

    if (!userId) {
      console.log('DramaDetail: 用户未登录，不发放分享积分');
      return;
    }

    // 调用分享积分 API（episode 对象只有 sort_order，无 id/_id/name）
    const episodeId = selectedEpisode
      ? (selectedEpisode.sort_order || selectedEpisode.id || selectedEpisode._id || 'all')
      : 'all';
    const result = await watchApi.shareDrama(userId, id, episodeId, platform);

    if (result.success && result.data?.points_awarded > 0) {
      setSharePointsSnackbar({
        open: true,
        points: result.data.points_awarded,
        message: result.data.message || `分享获得 ${result.data.points_awarded} 积分！`
      });
    }
  };

  const handleCloseShareSnackbar = () => {
    setSharePointsSnackbar(prev => ({ ...prev, open: false }));
  };

  const handleCloseFavoriteSnackbar = () => {
    setIsFavoriteSnackbarOpen(false);
  };

  // 新增：广告相关处理函数
  const handleCloseAd = () => {
    setShowAd(false);
  };

  const handleSkipAd = () => {
    setShowAd(false);
    if (selectedEpisode) {
      setIsPlayerOpen(true);
    }
  };

  const handleFavorite = async () => {
    // 检查登录状态
    if (!checkIsLoggedIn()) {
      setIsLoginRequiredDialogOpen(true);
      return;
    }

    // 获取用户ID（支持双系统）
    let userId = null;
    if (mongoUser && mongoUser._id) {
      userId = mongoUser._id;
    } else {
      const storedUser = localStorage.getItem('user');
      if (storedUser) {
        const user = JSON.parse(storedUser);
        if (user && user.id) {
          userId = user.id;
        }
      }
    }

    if (!userId) {
      console.log('无法获取用户ID');
      return;
    }

    try {
      // 如果已收藏，则取消收藏
      if (isFavorited) {
        await removeFavorite({ user_id: userId, drama_id: id });
        setIsFavorited(false);
        console.log('取消收藏成功');
      } else {
        // 调用新的MongoDB收藏API，保存用户收藏关系
        const result = await addFavorite({ user_id: userId, drama_id: id });
        if (result.success) {
          setIsFavorited(true);
          console.log('收藏成功:', result);
          
          // 显示收藏成功弹窗
          setIsFavoriteSnackbarOpen(true);

          // 3秒后自动隐藏弹窗
          setTimeout(() => {
            setIsFavoriteSnackbarOpen(false);
          }, 3000);

          // 收藏成功后，发放收藏积分
          handleFavoriteComplete();
        }
      }

      // 同时更新旧版API的收藏计数
      const response = await fetch(`/api/dramas/${id}/favorites`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
      });

      if (response.ok) {
        const data = await response.json();
        // 更新本地数据
        setDrama(prev => prev ? { ...prev, favoriteCount: data.favoriteCount } : null);
      }
    } catch (error) {
      console.error('收藏操作失败:', error);
    }
  };

  // 收藏积分发放逻辑
  const handleFavoriteComplete = async () => {
    try {
      // 支持双用户系统：MongoDB 用户(mongoUser)和旧版用户(user)
      let userId = null;
      if (mongoUser && mongoUser._id) {
        userId = mongoUser._id;
      } else {
        // 尝试从 localStorage 获取旧版用户
        const storedUser = localStorage.getItem('user');
        if (storedUser) {
          const user = JSON.parse(storedUser);
          if (user && user.id) {
            userId = user.id;
          }
        }
      }

      if (!userId) {
        console.log('未登录用户，跳过收藏积分发放');
        return;
      }

      console.log('开始发放收藏积分, userId:', userId, 'dramaId:', id);

      const result = await watchApi.favoriteDrama(userId, id);

      if (result && result.success && result.data) {
        const { points_awarded, is_first_favorite } = result.data;
        if (points_awarded > 0) {
          console.log(`收藏成功获得 ${points_awarded} 积分`);
          setFavoritePointsSnackbar({
            open: true,
            points: points_awarded,
            message: is_first_favorite ? `收藏成功，获得 ${points_awarded} 积分！` : '收藏成功！'
          });

          setTimeout(() => {
            setFavoritePointsSnackbar(prev => ({ ...prev, open: false }));
          }, 3000);
        } else {
          console.log('收藏但未获得积分（可能已收藏过）');
        }
      }
    } catch (error) {
      console.error('收藏积分发放失败:', error);
    }
  };

  const handleCommentSubmit = async () => {
    if (!newComment.trim()) {
      return;
    }

    setIsAddingComment(true);
    try {
      // 获取当前登录用户信息
      const user = getCurrentUser();
      const commentData = {
        content: newComment,
        userId: user ? user.id : null,
        userName: user ? user.name : null
      };

      const response = await fetch(`/api/dramas/${id}/comments`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(commentData),
      });

      if (response.ok) {
        setNewComment('');
        fetchComments(); // 重新加载评论
      } else {
        console.error('添加评论失败');
      }
    } catch (error) {
      console.error('添加评论失败:', error);
    }
    setIsAddingComment(false);
  };

  const handleEpisodeClick = (episode) => {
    console.log('handleEpisodeClick 被调用');
    console.log('当前广告状态:', advertisement);
    setSelectedEpisode(episode);
    
    if (advertisement) {
      console.log('广告存在，设置 showAd 为 true');
      setShowAd(true);
    } else {
      console.log('广告不存在，直接播放');
      setIsPlayerOpen(true);
    }
    
    // 记录观看历史 (新增功能)
    const historyItem = {
      dramaId: id,
      dramaTitle: drama.title,
      episodeNumber: episode.episodeNumber,
      episodeTitle: episode.title,
      timestamp: new Date().toISOString()
    };
    
    // 获取当前观看历史
    const existingHistory = JSON.parse(localStorage.getItem('watchHistory')) || [];
    
    // 检查是否已存在相同的剧集记录，如果有则更新
    const existingIndex = existingHistory.findIndex(item => 
      item.dramaId === historyItem.dramaId && 
      item.episodeNumber === historyItem.episodeNumber
    );
    
    if (existingIndex !== -1) {
      existingHistory[existingIndex] = historyItem;
    } else {
      existingHistory.push(historyItem);
    }
    
    // 保存到 localStorage
    localStorage.setItem('watchHistory', JSON.stringify(existingHistory));
  };

  const handleClosePlayer = () => {
    setIsPlayerOpen(false);
    setSelectedEpisode(null);
  };

  const handleAdClose = () => {
    setShowAd(false);
    setIsPlayerOpen(true);
  };

  const handleAdSkip = () => {
    setShowAd(false);
    setIsPlayerOpen(true);
  };

  if (isLoading) {
    return (
      <Container sx={{ py: 8 }}>
        <Typography variant="h4">加载中...</Typography>
      </Container>
    );
  }

  if (error) {
    return (
      <Container sx={{ py: 8 }}>
        <Typography variant="h4" color="error">
          加载失败: {error}
        </Typography>
      </Container>
    );
  }

  if (!drama) {
    return (
      <Container sx={{ py: 8 }}>
        <Typography variant="h4">未找到该剧集</Typography>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" disableGutters sx={{ mt: 2, mb: 4, px: 0 }}>
      {/* 新增：广告组件 */}
      <AdvertisementModal
        ad={advertisement}
        isOpen={showAd}
        onClose={handleAdClose}
        onSkip={handleAdSkip}
      />
      
      <Paper sx={{ p: { xs: 1, sm: 1.5 }, borderRadius: 2 }}>
        <Grid container spacing={4} alignItems="center">
          <Grid item xs={12} md={4} sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
            <Box sx={{ 
              width: '100%', 
              aspectRatio: '2/3', 
              borderRadius: '12px', 
              overflow: 'hidden', 
              boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)',
              backgroundImage: `url(${drama.poster || `/data/${drama.name}/poster.jpeg`})`,
              backgroundSize: 'cover',
              backgroundPosition: 'center'
            }}>
              {!drama.poster && (
                <Box sx={{
                  width: '100%',
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'center',
                  alignItems: 'center',
                  color: 'white',
                  backgroundColor: 'rgba(0, 0, 0, 0.7)',
                  padding: '20px',
                  textAlign: 'center'
                }}>
                  <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold' }}>
                    {drama.name}
                  </Typography>
                  <Typography variant="body2" sx={{ fontSize: '14px' }}>
                    暂无海报
                  </Typography>
                </Box>
              )}
            </Box>
          </Grid>
          
          <Grid item xs={12} md={8}>
            <Typography variant="h4" gutterBottom sx={{ fontWeight: 'bold', color: '#1976d2' }}>
              {drama.name}
            </Typography>
            
            <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
              <Box sx={{ 
                backgroundColor: '#f0f0f0', 
                padding: '4px 12px', 
                borderRadius: '4px', 
                fontSize: '14px' 
              }}>
                <Typography variant="body2" color="text.secondary">
                  {drama.category}
                </Typography>
              </Box>
              
              <Box sx={{ 
                backgroundColor: '#f0f0f0', 
                padding: '4px 12px', 
                borderRadius: '4px', 
                fontSize: '14px' 
              }}>
                <Typography variant="body2" color="text.secondary">
                  {drama.view_count}次观看
                </Typography>
              </Box>
              
              <Box sx={{ 
                backgroundColor: '#f0f0f0', 
                padding: '4px 12px', 
                borderRadius: '4px', 
                fontSize: '14px' 
              }}>
                <Typography variant="body2" color="text.secondary">
                  {drama.favorite_count}次收藏
                </Typography>
              </Box>
            </Box>
            
            <Typography variant="body1" paragraph sx={{ lineHeight: 1.6, fontSize: '16px' }}>
              {drama.description}
            </Typography>
            
            <Box sx={{ display: 'flex', gap: 2, mt: 3 }}>
              <Button 
                variant="contained" 
                startIcon={<FavoriteIcon />} 
                onClick={handleFavorite}
                sx={{ 
                  backgroundColor: isFavorited ? '#d32f2f' : '#e91e63', 
                  '&:hover': { backgroundColor: isFavorited ? '#b71c1c' : '#c2185b' },
                  padding: '10px 24px',
                  borderRadius: '24px',
                  fontWeight: 'bold'
                }}
              >
                {isFavorited ? '已收藏' : '收藏'}
              </Button>
              
              <Button 
                variant="outlined" 
                startIcon={<ShareIcon />} 
                onClick={handleShare}
                sx={{ 
                  color: '#1976d2', 
                  borderColor: '#1976d2',
                  '&:hover': { 
                    backgroundColor: '#e3f2fd', 
                    borderColor: '#1976d2' 
                  },
                  padding: '10px 24px',
                  borderRadius: '24px',
                  fontWeight: 'bold'
                }}
              >
                分享
              </Button>
            </Box>
          </Grid>
        </Grid>
        
        <Divider sx={{ my: 4 }} />
        
        <Typography variant="h5" sx={{ mb: 3, color: '#1976d2', fontWeight: 'bold' }}>
          剧集列表 ({drama.episodes?.length || 0} 集)
        </Typography>
        
        {/* 按 10 集为一组分组展示，组内横向滑动 */}
        {drama.episodes && drama.episodes.length > 0 && (() => {
          const groups = [];
          for (let i = 0; i < drama.episodes.length; i += 10) {
            groups.push(drama.episodes.slice(i, i + 10));
          }
          return groups.map((group, gi) => {
            const start = gi * 10 + 1;
            const end = gi * 10 + group.length;
            return (
              <Box key={`ep-group-${gi}`} sx={{ mb: 4 }}>
                <Typography
                  variant="subtitle1"
                  sx={{
                    mb: 1.5,
                    fontWeight: 'bold',
                    color: '#333',
                    display: 'flex',
                    alignItems: 'center',
                    '&::before': {
                      content: '""',
                      display: 'inline-block',
                      width: 4,
                      height: 16,
                      backgroundColor: '#1976d2',
                      borderRadius: 2,
                      mr: 1,
                    },
                  }}
                >
                  {start}集 - {end}集
                </Typography>
                <Box
                  sx={{
                    display: 'flex',
                    overflowX: 'auto',
                    gap: 1.5,
                    pb: 1.5,
                    px: 0.5,
                    scrollSnapType: 'x mandatory',
                    WebkitOverflowScrolling: 'touch',
                    '&::-webkit-scrollbar': { height: 6 },
                    '&::-webkit-scrollbar-thumb': {
                      backgroundColor: 'rgba(25,118,210,0.3)',
                      borderRadius: 3,
                    },
                    '&::-webkit-scrollbar-track': { backgroundColor: 'transparent' },
                  }}
                >
                  {group.map((episode, idx) => {
                    const epNo = gi * 10 + idx + 1;
                    return (
                      <Box
                        key={episode.episodeNumber || episode.id || epNo}
                        data-testid={`episode-card-${episode.id}`}
                        onClick={(e) => {
                          console.log('DramaDetail 页面 - 剧集点击事件:', episode.id);
                          console.log('事件对象:', e);
                          handleEpisodeClick(episode);
                        }}
                        sx={{
                          flex: '0 0 auto',
                          width: { xs: 108, sm: 126 },
                          cursor: 'pointer',
                          scrollSnapAlign: 'start',
                          borderRadius: 1.5,
                          overflow: 'hidden',
                          backgroundColor: '#fff',
                          boxShadow: '0 1px 4px rgba(0,0,0,0.08)',
                          transition: 'transform 0.2s ease, box-shadow 0.2s ease',
                          '&:hover': {
                            transform: 'translateY(-3px)',
                            boxShadow: '0 6px 16px rgba(25,118,210,0.25)',
                          },
                        }}
                      >
                        <Box
                          sx={{
                            position: 'relative',
                            width: '100%',
                            paddingTop: '140%',
                            backgroundImage: `url(${episode.image_url || drama.coverUrl || drama.cover || ''})`,
                            backgroundSize: 'contain',
                            backgroundRepeat: 'no-repeat',
                            backgroundPosition: 'center',
                            backgroundColor: '#000',
                          }}
                        >
                          <Box
                            sx={{
                              position: 'absolute',
                              top: 6,
                              left: '50%',
                              transform: 'translateX(-50%)',
                              backgroundColor: 'rgba(0,0,0,0.6)',
                              color: '#fff',
                              fontSize: '0.7rem',
                              fontWeight: 'bold',
                              px: 0.8,
                              py: 0.2,
                              borderRadius: 0.5,
                              textAlign: 'center',
                              whiteSpace: 'nowrap',
                            }}
                          >
                            第{epNo}集
                          </Box>
                        </Box>
                      </Box>
                    );
                  })}
                </Box>
              </Box>
            );
          });
        })()}
        
        <Divider sx={{ my: 4 }} />
        
        <Typography variant="h5" sx={{ mb: 3, color: '#1976d2', fontWeight: 'bold' }}>
          评论 ({comments.length})
        </Typography>
        
        <Box sx={{ mb: 3 }}>
          <TextField
            fullWidth
            placeholder="写下你的评论..."
            value={newComment}
            onChange={(e) => setNewComment(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && handleCommentSubmit()}
            sx={{ mb: 2 }}
          />
          <Button 
            variant="contained" 
            onClick={handleCommentSubmit}
            disabled={isAddingComment || !newComment.trim()}
            sx={{ 
              backgroundColor: '#1976d2',
              '&:hover': { backgroundColor: '#1565c0' },
              borderRadius: '24px',
              padding: '10px 24px',
              fontWeight: 'bold'
            }}
          >
            {isAddingComment ? '发表中...' : '发表评论'}
          </Button>
        </Box>
        
        <Box sx={{ mb: 4 }}>
          {comments.map((comment, index) => (
            <Paper key={index} sx={{ p: 2, mb: 2, borderRadius: 1.5 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <Box sx={{ 
                  width: 40, 
                  height: 40, 
                  borderRadius: '50%', 
                  backgroundColor: '#f0f0f0', 
                  mr: 2,
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  color: '#1976d2',
                  fontWeight: 'bold'
                }}>
                  {(comment.userName || comment.userId || '游客').charAt(0).toUpperCase()}
                </Box>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
                    {comment.userName || comment.userId || '游客'}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {new Date(comment.createdAt).toLocaleString()}
                  </Typography>
                </Box>
              </Box>
              <Typography variant="body1" sx={{ ml: 7 }}>
                {comment.content}
              </Typography>
            </Paper>
          ))}
        </Box>
        
        <Dialog
          open={isPlayerOpen}
          onClose={handleClosePlayer}
          maxWidth="lg"
          fullWidth
          PaperProps={{
            sx: {
              borderRadius: '12px',
              overflow: 'hidden'
            }
          }}
        >
          <DialogTitle sx={{ 
            padding: '16px 24px', 
            borderBottom: '1px solid #e0e0e0',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}>
            <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
              {selectedEpisode?.title}
            </Typography>
            <Button onClick={handleClosePlayer} startIcon={<CloseIcon />}>
              关闭
            </Button>
          </DialogTitle>
          <DialogContent sx={{ paddingTop: 0 }}>
            <Box sx={{ width: '100%', display: 'flex', justifyContent: 'center' }}>
              {selectedEpisode && (
                <video
                  ref={videoRef}
                  controls
                  style={{
                    maxWidth: '100%',
                    height: 'auto',
                    borderRadius: '8px',
                  }}
                  autoPlay
                >
                  <source
                    src={selectedEpisode.video_url}
                    type="video/mp4"
                  />
                  您的浏览器不支持视频播放。
                </video>
              )}
            </Box>
          </DialogContent>
        </Dialog>

        {/* 新增：分享弹窗 */}
      <ShareDialog
        open={isShareDialogOpen}
        onClose={handleCloseShareDialog}
        dramaTitle={drama.name}
        dramaId={drama.id || id}
        onShare={handleShareComplete}
      />

        {/* 新增：广告组件 */}
        <AdvertisementModal
          ad={advertisement}
          isOpen={showAd}
          onClose={() => setShowAd(false)}
          onSkip={() => {
            setShowAd(false);
            if (selectedEpisode) {
              setIsPlayerOpen(true);
            }
          }}
        />

        {/* 新增：收藏成功弹窗 */}
        <Snackbar
          open={isFavoriteSnackbarOpen}
          autoHideDuration={3000}
          onClose={handleCloseFavoriteSnackbar}
          anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
          sx={{
            '& .MuiSnackbarContent-root': {
              backgroundColor: '#4caf50',
              borderRadius: '8px',
              boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)',
              padding: '12px 20px',
              display: 'flex',
              alignItems: 'center',
              gap: '12px'
            }
          }}
        >
          <Alert 
            severity="success" 
            sx={{ 
              bgcolor: 'transparent', 
              color: 'white',
              border: 'none',
              '& .MuiAlert-icon': { color: 'white' },
              '& .MuiAlert-message': { 
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                padding: 0
              }
            }}
            icon={<FavoriteIcon sx={{ color: 'white' }} />}
          >
            <div style={{ fontWeight: '600', fontSize: '16px' }}>已收藏！</div>
            <div style={{ opacity: 0.9, fontSize: '14px' }}>该剧集已成功添加到您的收藏列表</div>
          </Alert>
        </Snackbar>

        {/* 分享获得积分提示 */}
        <Snackbar
          open={sharePointsSnackbar.open}
          autoHideDuration={3000}
          onClose={handleCloseShareSnackbar}
          anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        >
          <Alert
            onClose={handleCloseShareSnackbar}
            severity="success"
            sx={{ width: '100%' }}
          >
            🎉 {sharePointsSnackbar.message}
          </Alert>
        </Snackbar>

      {/* 收藏获得积分提示 */}
      <Snackbar
        open={favoritePointsSnackbar.open}
        autoHideDuration={3000}
        onClose={() => setFavoritePointsSnackbar(prev => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert 
          severity="success" 
          variant="filled"
          sx={{ width: '100%' }}
        >
          {favoritePointsSnackbar.message || `收藏成功，获得 ${favoritePointsSnackbar.points} 积分！`}
        </Alert>
      </Snackbar>

      {/* 登录提示弹窗 */}
      <Dialog
        open={isLoginRequiredDialogOpen}
        onClose={() => setIsLoginRequiredDialogOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <FavoriteIcon color="primary" />
            <Typography variant="h6" component="span">
              需要登录
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ py: 1 }}>
            <Typography variant="body1" color="text.primary" gutterBottom>
              登录后才能收藏剧集
            </Typography>
            <Typography variant="body2" color="text.secondary">
              登录后还可以享受更多功能，包括：
            </Typography>
            <Box component="ul" sx={{ mt: 1, pl: 2 }}>
              <Typography component="li" variant="body2" color="text.secondary">
                收藏喜爱的剧集
              </Typography>
              <Typography component="li" variant="body2" color="text.secondary">
                发表评论和互动
              </Typography>
              <Typography component="li" variant="body2" color="text.secondary">
                观看剧集获得积分
              </Typography>
              <Typography component="li" variant="body2" color="text.secondary">
                分享剧集获得奖励
              </Typography>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button 
            onClick={() => setIsLoginRequiredDialogOpen(false)}
            color="inherit"
          >
            取消
          </Button>
          <Button 
            variant="contained"
            color="primary"
            onClick={() => {
              setIsLoginRequiredDialogOpen(false);
              // 跳转到登录页，优先使用 MongoDB 登录系统
              window.location.href = '/login-v2';
            }}
            startIcon={<FavoriteIcon />}
          >
            去登录
          </Button>
        </DialogActions>
      </Dialog>
      </Paper>

      {/* 广告组件 */}
      {advertisement && (
        <AdvertisementModal
          ad={advertisement}
          isOpen={showAd}
          onClose={() => setShowAd(false)}
          onSkip={() => {
            setShowAd(false);
            setIsPlayerOpen(true);
          }}
        />
      )}
    </Container>
  );
};

export default DramaDetail;